import { useState, useCallback, useEffect } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { apiClient } from '@/lib/api-client';

export interface GitHubRepository {
  id: number;
  name: string;
  full_name: string;
  description?: string;
  private: boolean;
  html_url: string;
  clone_url: string;
  default_branch: string;
  updated_at: string;
  owner: {
    login: string;
    avatar_url: string;
  };
}

export interface GitHubCommit {
  sha: string;
  message: string;
  author: {
    name: string;
    email: string;
    date: string;
  };
  html_url: string;
}

export interface GitHubConnection {
  isConnected: boolean;
  username?: string;
  avatar_url?: string;
  repositories: GitHubRepository[];
  selectedRepository?: GitHubRepository;
}

export interface GitHubIntegrationState {
  connection: GitHubConnection;
  isLoading: boolean;
  error?: string;
  commits: GitHubCommit[];
  isLoadingCommits: boolean;
  isPushing: boolean;
  isPulling: boolean;
}

export function useGitHubIntegration(agentId?: string) {
  const { user, isAuthenticated } = useAuth();
  const [state, setState] = useState<GitHubIntegrationState>({
    connection: {
      isConnected: false,
      repositories: []
    },
    isLoading: false,
    commits: [],
    isLoadingCommits: false,
    isPushing: false,
    isPulling: false
  });

  // Connect to GitHub (initiate OAuth flow)
  const connectToGitHub = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setState(prev => ({ ...prev, error: 'Authentication required' }));
      return;
    }

    setState(prev => ({ ...prev, isLoading: true, error: undefined }));

    try {
      // For now, we'll use the existing GitHub export OAuth flow
      // In a full implementation, this would be a separate OAuth flow for repository access
      const response = await apiClient.initiateGitHubExport({
        repositoryName: 'temp-connection-test',
        description: 'Temporary repository for GitHub connection',
        isPrivate: true,
        agentId: agentId || 'temp'
      });

      if (response.success && response.data?.authUrl) {
        // Redirect to GitHub OAuth
        window.location.href = response.data.authUrl;
      } else {
        throw new Error('Failed to initiate GitHub connection');
      }
    } catch (error) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to connect to GitHub'
      }));
    }
  }, [isAuthenticated, user, agentId]);

  // Load user's repositories
  const loadRepositories = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true, error: undefined }));

    try {
      // This would call a new API endpoint to fetch user's repositories
      // For now, we'll simulate this functionality
      const mockRepositories: GitHubRepository[] = [
        {
          id: 1,
          name: 'my-app',
          full_name: 'user/my-app',
          description: 'My awesome application',
          private: false,
          html_url: 'https://github.com/user/my-app',
          clone_url: 'https://github.com/user/my-app.git',
          default_branch: 'main',
          updated_at: new Date().toISOString(),
          owner: {
            login: 'user',
            avatar_url: 'https://github.com/user.png'
          }
        }
      ];

      setState(prev => ({
        ...prev,
        isLoading: false,
        connection: {
          ...prev.connection,
          isConnected: true,
          username: user?.name || 'User',
          repositories: mockRepositories
        }
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to load repositories'
      }));
    }
  }, [user]);

  // Select a repository
  const selectRepository = useCallback((repository: GitHubRepository) => {
    setState(prev => ({
      ...prev,
      connection: {
        ...prev.connection,
        selectedRepository: repository
      }
    }));
  }, []);

  // Load commit history for selected repository
  const loadCommitHistory = useCallback(async () => {
    const { selectedRepository } = state.connection;
    if (!selectedRepository) return;

    setState(prev => ({ ...prev, isLoadingCommits: true }));

    try {
      // This would call GitHub API to fetch commit history
      // For now, we'll simulate this functionality
      const mockCommits: GitHubCommit[] = [
        {
          sha: 'abc123',
          message: 'Initial commit',
          author: {
            name: 'User',
            email: 'user@example.com',
            date: new Date().toISOString()
          },
          html_url: `${selectedRepository.html_url}/commit/abc123`
        }
      ];

      setState(prev => ({
        ...prev,
        isLoadingCommits: false,
        commits: mockCommits
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        isLoadingCommits: false,
        error: error instanceof Error ? error.message : 'Failed to load commit history'
      }));
    }
  }, [state.connection]);

  // Push changes to GitHub
  const pushToGitHub = useCallback(async (commitMessage: string) => {
    const { selectedRepository } = state.connection;
    if (!selectedRepository || !agentId) return;

    setState(prev => ({ ...prev, isPushing: true, error: undefined }));

    try {
      // This would use the existing GitHub push functionality
      // For now, we'll simulate this with the commit message
      console.log('Pushing with commit message:', commitMessage);
      await new Promise(resolve => setTimeout(resolve, 2000));

      setState(prev => ({ ...prev, isPushing: false }));

      // Reload commit history after successful push
      await loadCommitHistory();
    } catch (error) {
      setState(prev => ({
        ...prev,
        isPushing: false,
        error: error instanceof Error ? error.message : 'Failed to push to GitHub'
      }));
    }
  }, [state.connection, agentId, loadCommitHistory]);

  // Pull changes from GitHub
  const pullFromGitHub = useCallback(async () => {
    const { selectedRepository } = state.connection;
    if (!selectedRepository) return;

    setState(prev => ({ ...prev, isPulling: true, error: undefined }));

    try {
      // This would fetch latest changes from GitHub and update local files
      // For now, we'll simulate this
      await new Promise(resolve => setTimeout(resolve, 2000));

      setState(prev => ({ ...prev, isPulling: false }));
      
      // Reload commit history after successful pull
      await loadCommitHistory();
    } catch (error) {
      setState(prev => ({
        ...prev,
        isPulling: false,
        error: error instanceof Error ? error.message : 'Failed to pull from GitHub'
      }));
    }
  }, [state.connection, loadCommitHistory]);

  // Disconnect from GitHub
  const disconnect = useCallback(() => {
    setState({
      connection: {
        isConnected: false,
        repositories: []
      },
      isLoading: false,
      commits: [],
      isLoadingCommits: false,
      isPushing: false,
      isPulling: false
    });
  }, []);

  // Load repositories when connected
  useEffect(() => {
    if (state.connection.isConnected && state.connection.repositories.length === 0) {
      loadRepositories();
    }
  }, [state.connection.isConnected, state.connection.repositories.length, loadRepositories]);

  // Load commit history when repository is selected
  useEffect(() => {
    if (state.connection.selectedRepository) {
      loadCommitHistory();
    }
  }, [state.connection.selectedRepository, loadCommitHistory]);

  return {
    ...state,
    connectToGitHub,
    loadRepositories,
    selectRepository,
    loadCommitHistory,
    pushToGitHub,
    pullFromGitHub,
    disconnect,
    isAuthenticated,
    user
  };
}
